/**
 * NDC suggestion predicate (spec §9.1 — patch D7).
 *
 * "NDC Issued — manual date. System suggests after every part reaches
 * stage 18."
 *
 * The banner shows when ALL of:
 *   1. The project has at least one live PART (suggesting NDC on an
 *      empty project would be a UI noise event)
 *   2. Every live PART has currentStageNumber === 18
 *   3. warrantyEndDate is known and has passed or is today
 *   4. ndcIssuedDate is null (not already issued)
 *   5. projectClosedAt is null (closed projects don't show suggestions
 *      — the action would just clutter the closed-project read-only view)
 *
 * SUB_PARTs are NOT considered directly — a sub-part's progression
 * drives its parent PART's currentStage, which is what stage 18 means
 * for the parent. Treating SUB_PARTs as their own units would either
 * gate twice or never gate (since they max at the batch level).
 *
 * Pure, no React, no API access — easy to test.
 */
import type { Project } from './project-queries';
import type { ProjectItem } from './pipeline-queries';

function todayPktIsoDate(): string {
  const pkt = new Date(Date.now() + 5 * 60 * 60 * 1000);
  return pkt.toISOString().slice(0, 10);
}

export function shouldSuggestNdc(project: Project, items: readonly ProjectItem[]): boolean {
  if (project.ndcIssuedDate !== null) return false;
  if (project.projectClosedAt !== null) return false;
  if (project.warrantyEndDate === null) return false;

  const today = todayPktIsoDate();
  if (project.warrantyEndDate > today) return false;

  const liveParts = items.filter((i) => i.itemType === 'PART' && i.deletedAt === null);
  if (liveParts.length === 0) return false;
  return liveParts.every((p) => p.currentStageNumber === 18);
}
